/**
 * FileManagerCard — browse / upload / download / delete objects in the app's
 * Firebase Storage bucket (web-construct-app.appspot.com), rendered as a
 * Metronic card on the File Explorer page (/file-explorer, linked from
 * Settings).
 *
 * GCS has no real folders: "folders" here are the prefixes returned by list(),
 * and "New folder" plants a zero-byte `.keep` object so the prefix exists
 * before its first real upload. `.keep` objects are hidden from the listing
 * but are included when a folder is deleted.
 *
 * Operational prefixes (mail/, andrei_attachments/, ckeditor_uploads/) hold
 * files that Firestore documents and rich-text content point at by URL, so
 * deleting from them would silently break emails / chat history / editor
 * content. Those trees are browse-and-download only — no upload, no delete,
 * no new folders.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ref,
  list,
  listAll,
  getMetadata,
  getDownloadURL,
  deleteObject,
  uploadBytes,
  uploadBytesResumable,
  UploadTask,
} from 'firebase/storage';
import { storage } from '../../../firebase';
import { toast } from 'react-toastify';
import { fmtBytes, fmtDate, fileIcon, isProtected, joinPath } from './storageShared';

interface FileEntry {
  name: string;
  fullPath: string;
  size?: number;
  updated?: string;
  contentType?: string;
}

interface UploadEntry {
  id: string;
  name: string;
  pct: number;
  state: 'running' | 'done' | 'error';
}

interface FileManagerCardProps {
  /** Bump nonce to force a jump even when the path is unchanged. */
  jumpTo?: { path: string; nonce: number } | null;
}

const PAGE_SIZE = 200;
// Refuse recursive folder deletes above this many objects — that's a job for
// the Firebase console / gsutil, not a browser tab.
const FOLDER_DELETE_CAP = 500;

// Every object path under `p`, recursively (listAll already paginates
// internally; recursion is only needed for sub-prefixes). Includes `.keep`s.
async function collectAllFiles(p: string): Promise<string[]> {
  const res = await listAll(ref(storage, p));
  let paths = res.items.map((i) => i.fullPath);
  for (const pre of res.prefixes) {
    paths = paths.concat(await collectAllFiles(pre.fullPath));
  }
  return paths;
}

const FileManagerCard: React.FC<FileManagerCardProps> = ({ jumpTo }) => {
  const [path, setPath] = useState<string>('');
  const [folders, setFolders] = useState<string[]>([]);
  const [files, setFiles] = useState<FileEntry[]>([]);
  const [pageToken, setPageToken] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [filter, setFilter] = useState<string>('');
  const [uploads, setUploads] = useState<UploadEntry[]>([]);
  const [busyPath, setBusyPath] = useState<string>('');
  const [dragOver, setDragOver] = useState<boolean>(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const tasksRef = useRef<Map<string, UploadTask>>(new Map());
  const runningRef = useRef<number>(0);

  const readOnly = isProtected(path);
  const bucket = (storage.app.options as { storageBucket?: string }).storageBucket || '';

  const load = useCallback(async (p: string, token?: string) => {
    setLoading(true);
    try {
      const res = await list(ref(storage, p), { maxResults: PAGE_SIZE, pageToken: token });
      const visible = res.items.filter((i) => i.name !== '.keep');
      // Metadata is one request per object; allSettled so a single failure
      // doesn't blank the listing.
      const metas = await Promise.allSettled(visible.map((i) => getMetadata(i)));
      const entries: FileEntry[] = visible.map((i, idx) => {
        const m = metas[idx];
        const ok = m.status === 'fulfilled' ? m.value : undefined;
        return {
          name: i.name,
          fullPath: i.fullPath,
          size: ok ? Number(ok.size) : undefined,
          updated: ok ? ok.updated : undefined,
          contentType: ok ? ok.contentType || undefined : undefined,
        };
      });
      entries.sort((a, b) => a.name.localeCompare(b.name));
      const newFolders = res.prefixes.map((pre) => pre.name).sort((a, b) => a.localeCompare(b));
      if (token) {
        setFolders((prev) => prev.concat(newFolders));
        setFiles((prev) => prev.concat(entries));
      } else {
        setFolders(newFolders);
        setFiles(entries);
      }
      setPageToken(res.nextPageToken);
    } catch (e: any) {
      toast.error(`Couldn't list storage: ${e?.message || e}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(path);
  }, [path, load]);

  // Jump requests from the usage card ("open in file manager").
  useEffect(() => {
    if (jumpTo) {
      setFilter('');
      setPath(jumpTo.path);
    }
  }, [jumpTo]);

  const navigateTo = (p: string) => {
    setFilter('');
    setPath(p);
  };

  // ---- uploads -------------------------------------------------------------

  const startUploads = (fileList: FileList | File[]) => {
    if (readOnly) {
      toast.error('This folder is operational (referenced by app data) — read-only here.');
      return;
    }
    const arr = Array.from(fileList);
    if (!arr.length) return;
    const existing = new Set(files.map((f) => f.name));
    const clashes = arr.filter((f) => existing.has(f.name)).map((f) => f.name);
    if (clashes.length) {
      const shown = clashes.slice(0, 5).join(', ') + (clashes.length > 5 ? ', …' : '');
      if (!window.confirm(`Overwrite ${clashes.length} existing file(s)?\n${shown}`)) return;
    }
    arr.forEach((file) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2)}-${file.name}`;
      const task = uploadBytesResumable(ref(storage, joinPath(path, file.name)), file);
      tasksRef.current.set(id, task);
      runningRef.current += 1;
      setUploads((prev) => prev.concat({ id, name: file.name, pct: 0, state: 'running' }));

      const finish = (state: 'done' | 'error') => {
        tasksRef.current.delete(id);
        runningRef.current -= 1;
        setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, state, pct: state === 'done' ? 100 : u.pct } : u)));
        if (runningRef.current === 0) {
          load(path);
          // Let the finished rows linger briefly, then clear.
          setTimeout(() => setUploads((prev) => prev.filter((u) => u.state === 'running')), 4000);
        }
      };

      task.on(
        'state_changed',
        (snap) => {
          const pct = snap.totalBytes ? Math.round((snap.bytesTransferred / snap.totalBytes) * 100) : 0;
          setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, pct } : u)));
        },
        (err) => {
          if (err.code !== 'storage/canceled') toast.error(`Upload failed: ${file.name} — ${err.message}`);
          finish('error');
        },
        () => finish('done')
      );
    });
  };

  const cancelUpload = (id: string) => {
    tasksRef.current.get(id)?.cancel();
  };

  // ---- per-file actions ----------------------------------------------------

  const openFile = async (f: FileEntry) => {
    setBusyPath(f.fullPath);
    try {
      const url = await getDownloadURL(ref(storage, f.fullPath));
      window.open(url, '_blank', 'noopener');
    } catch (e: any) {
      toast.error(`Couldn't open: ${e?.message || e}`);
    } finally {
      setBusyPath('');
    }
  };

  const copyUrl = async (f: FileEntry) => {
    setBusyPath(f.fullPath);
    try {
      const url = await getDownloadURL(ref(storage, f.fullPath));
      await navigator.clipboard.writeText(url);
      toast.success('Download URL copied');
    } catch (e: any) {
      toast.error(`Couldn't copy URL: ${e?.message || e}`);
    } finally {
      setBusyPath('');
    }
  };

  const deleteFile = async (f: FileEntry) => {
    if (!window.confirm(`Delete "${f.fullPath}"?\nThis cannot be undone.`)) return;
    setBusyPath(f.fullPath);
    try {
      await deleteObject(ref(storage, f.fullPath));
      setFiles((prev) => prev.filter((x) => x.fullPath !== f.fullPath));
      toast.success(`Deleted ${f.name}`);
    } catch (e: any) {
      toast.error(`Delete failed: ${e?.message || e}`);
    } finally {
      setBusyPath('');
    }
  };

  const deleteFolder = async (name: string) => {
    const target = joinPath(path, name);
    setBusyPath(target);
    try {
      const all = await collectAllFiles(target);
      if (all.length > FOLDER_DELETE_CAP) {
        toast.error(`"${name}" holds ${all.length} objects — over the ${FOLDER_DELETE_CAP} cap. Use the Firebase console for bulk deletes.`);
        return;
      }
      if (!window.confirm(`Delete folder "${name}" and all ${all.length} file(s) inside it?\nThis cannot be undone.`)) return;
      let failed = 0;
      for (let i = 0; i < all.length; i += 15) {
        const chunk = all.slice(i, i + 15);
        const results = await Promise.allSettled(chunk.map((p) => deleteObject(ref(storage, p))));
        failed += results.filter((r) => r.status === 'rejected').length;
      }
      if (failed) toast.warn(`Folder removed with ${failed} failure(s) — refresh to see what's left.`);
      else toast.success(`Deleted folder ${name} (${all.length} files)`);
      load(path);
    } catch (e: any) {
      toast.error(`Folder delete failed: ${e?.message || e}`);
    } finally {
      setBusyPath('');
    }
  };

  const newFolder = async () => {
    if (readOnly) return;
    const name = window.prompt('New folder name');
    if (!name) return;
    const clean = name.trim().replace(/[/\\]/g, '-');
    if (!clean) return;
    try {
      // Zero-byte placeholder so the prefix exists before its first upload.
      await uploadBytes(ref(storage, `${joinPath(path, clean)}/.keep`), new Blob([]));
      navigateTo(joinPath(path, clean));
    } catch (e: any) {
      toast.error(`Couldn't create folder: ${e?.message || e}`);
    }
  };

  // ---- render --------------------------------------------------------------

  const crumbs = path ? path.split('/') : [];
  const q = filter.trim().toLowerCase();
  const shownFolders = q ? folders.filter((f) => f.toLowerCase().indexOf(q) !== -1) : folders;
  const shownFiles = q ? files.filter((f) => f.name.toLowerCase().indexOf(q) !== -1) : files;
  const totalSize = files.reduce((acc, f) => acc + (f.size || 0), 0);

  return (
    <div className='card'>
      <div className='card-header border-0 pt-6'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>
            File manager
            {readOnly && <span className='badge badge-light-warning ms-3 fs-8'>operational — read-only</span>}
          </span>
          <span className='text-muted mt-1 fw-semibold fs-7'>{bucket}</span>
        </h3>
        <div className='card-toolbar gap-2'>
          <input
            type='text'
            className='form-control form-control-sm form-control-solid w-150px'
            placeholder='Filter…'
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <button type='button' className='btn btn-sm btn-light' onClick={() => load(path)} disabled={loading} title='Refresh'>
            <i className='bi bi-arrow-clockwise' />
          </button>
          <button type='button' className='btn btn-sm btn-light' onClick={newFolder} disabled={readOnly || loading}>
            <i className='bi bi-folder-plus me-1' />
            New folder
          </button>
          <button type='button' className='btn btn-sm btn-primary' onClick={() => inputRef.current?.click()} disabled={readOnly}>
            <i className='bi bi-upload me-1' />
            Upload
          </button>
          <input
            ref={inputRef}
            type='file'
            multiple
            className='d-none'
            onChange={(e) => {
              if (e.target.files) startUploads(e.target.files);
              e.target.value = '';
            }}
          />
        </div>
      </div>

      <div
        className='card-body pt-2'
        onDragOver={(e) => {
          e.preventDefault();
          if (!readOnly) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer.files.length) startUploads(e.dataTransfer.files);
        }}
        style={dragOver ? { outline: '2px dashed var(--bs-primary)', outlineOffset: '-8px', borderRadius: 8 } : undefined}
      >
        {/* breadcrumbs */}
        <div className='d-flex align-items-center flex-wrap mb-4 fs-6'>
          <button type='button' className='btn btn-link p-0 fw-semibold' onClick={() => navigateTo('')} disabled={!path}>
            <i className='bi bi-hdd me-1' />
            Storage
          </button>
          {crumbs.map((c, i) => {
            const target = crumbs.slice(0, i + 1).join('/');
            const last = i === crumbs.length - 1;
            return (
              <React.Fragment key={target}>
                <span className='text-muted mx-2'>/</span>
                {last ? (
                  <span className='fw-bold'>{c}</span>
                ) : (
                  <button type='button' className='btn btn-link p-0 fw-semibold' onClick={() => navigateTo(target)}>
                    {c}
                  </button>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* in-flight uploads */}
        {uploads.length > 0 && (
          <div className='mb-4'>
            {uploads.map((u) => (
              <div key={u.id} className='d-flex align-items-center gap-3 mb-1'>
                <span className='fs-7 text-truncate' style={{ maxWidth: 260 }}>
                  {u.name}
                </span>
                <div className='progress flex-grow-1' style={{ height: 6 }}>
                  <div
                    className={`progress-bar ${u.state === 'error' ? 'bg-danger' : u.state === 'done' ? 'bg-success' : ''}`}
                    style={{ width: `${u.pct}%` }}
                  />
                </div>
                <span className='fs-8 text-muted w-40px text-end'>{u.state === 'error' ? 'failed' : `${u.pct}%`}</span>
                {u.state === 'running' && (
                  <button type='button' className='btn btn-icon btn-sm btn-light h-20px w-20px' onClick={() => cancelUpload(u.id)} title='Cancel'>
                    <i className='bi bi-x fs-7' />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {loading && folders.length === 0 && files.length === 0 ? (
          <div className='text-muted py-10 text-center'>
            <span className='spinner-border spinner-border-sm me-2' />
            Loading…
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-200 align-middle gs-0 gy-2'>
              <thead>
                <tr className='fw-bold text-muted fs-7 text-uppercase'>
                  <th>Name</th>
                  <th className='w-100px'>Size</th>
                  <th className='w-175px'>Modified</th>
                  <th className='w-125px text-end'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {shownFolders.map((name) => {
                  const target = joinPath(path, name);
                  const protectedRoot = isProtected(target);
                  return (
                    <tr key={`d:${name}`}>
                      <td>
                        <button type='button' className='btn btn-link p-0 text-start fw-semibold' onClick={() => navigateTo(target)}>
                          <i className='bi bi-folder-fill text-warning me-2' />
                          {name}
                          {protectedRoot && <span className='badge badge-light-warning ms-2 fs-9'>operational</span>}
                        </button>
                      </td>
                      <td className='text-muted'>—</td>
                      <td className='text-muted'>—</td>
                      <td className='text-end'>
                        {!protectedRoot && !readOnly && (
                          <button
                            type='button'
                            className='btn btn-icon btn-sm btn-light-danger'
                            onClick={() => deleteFolder(name)}
                            disabled={busyPath === target}
                            title='Delete folder and contents'
                          >
                            {busyPath === target ? <span className='spinner-border spinner-border-sm' /> : <i className='bi bi-trash' />}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {shownFiles.map((f) => (
                  <tr key={`f:${f.fullPath}`}>
                    <td>
                      <button type='button' className='btn btn-link p-0 text-start' onClick={() => openFile(f)} title='Open in new tab'>
                        <i className={`bi ${fileIcon(f.name, f.contentType)} me-2`} />
                        {f.name}
                      </button>
                    </td>
                    <td className='text-muted fs-7'>{fmtBytes(f.size)}</td>
                    <td className='text-muted fs-7'>{fmtDate(f.updated)}</td>
                    <td className='text-end'>
                      <div className='d-flex justify-content-end gap-1'>
                        <button
                          type='button'
                          className='btn btn-icon btn-sm btn-light'
                          onClick={() => copyUrl(f)}
                          disabled={busyPath === f.fullPath}
                          title='Copy download URL'
                        >
                          <i className='bi bi-link-45deg' />
                        </button>
                        <button
                          type='button'
                          className='btn btn-icon btn-sm btn-light'
                          onClick={() => openFile(f)}
                          disabled={busyPath === f.fullPath}
                          title='Download / open'
                        >
                          <i className='bi bi-box-arrow-down' />
                        </button>
                        {!readOnly && (
                          <button
                            type='button'
                            className='btn btn-icon btn-sm btn-light-danger'
                            onClick={() => deleteFile(f)}
                            disabled={busyPath === f.fullPath}
                            title='Delete'
                          >
                            {busyPath === f.fullPath ? <span className='spinner-border spinner-border-sm' /> : <i className='bi bi-trash' />}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!loading && shownFolders.length === 0 && shownFiles.length === 0 && (
                  <tr>
                    <td colSpan={4} className='text-muted text-center py-8'>
                      {q ? 'Nothing matches the filter.' : readOnly ? 'Empty folder.' : 'Empty folder — drop files here or hit Upload.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className='d-flex align-items-center justify-content-between mt-2'>
          <span className='text-muted fs-8'>
            {folders.length} folder{folders.length === 1 ? '' : 's'} · {files.length} file{files.length === 1 ? '' : 's'}
            {totalSize > 0 && ` · ${fmtBytes(totalSize)}`}
          </span>
          {pageToken && (
            <button type='button' className='btn btn-sm btn-light' onClick={() => load(path, pageToken)} disabled={loading}>
              {loading ? <span className='spinner-border spinner-border-sm me-2' /> : null}
              Load more
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileManagerCard;
